import { useCodingPreview } from './useCodingDraftPreview';
import { useEffect, useMemo, useState } from 'react';
import { useJsonForms } from '@jsonforms/react';
import type { ControlProps } from '@jsonforms/core';
import type { MeddraRelease, MeddraTerm } from '@/lib/api/generated/casedb';
import { useCaseFieldReview } from '../../shared/hooks/useCaseFieldReview';
import { CODING_ACTION, CODING_STATUS_LABELS, codingCommand, codingFields, parseCoding, type CodingState } from '@/features/meddra/shared/model/meddraCoding';
import { dictionaryReleases, dictionarySearch, dictionaryTerm } from '../services/meddraApi';

export function useMeddraCoding(props: ControlProps) {
  const field = useCaseFieldReview(props);
  const sharedPreview = useCodingPreview();
  const form = useJsonForms();
  const record = (form.core?.data ?? {}) as Record<string, unknown>;
  const fields = codingFields(field.fieldId);
  const verbatim = String(record[fields.source] ?? '');
  const parsed = useMemo(() => {
    try { return { state: parseCoding(props.data), error: '' }; }
    catch (error) { return { state: {} as CodingState, error: String(error) }; }
  }, [props.data]);
  const state = parsed.state;
  const [dialog, setDialog] = useState<'search' | 'manual' | 'history' | null>(null);
  const [releases, setReleases] = useState<MeddraRelease[]>([]);
  const [version, setVersion] = useState(state.version ?? '');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [terms, setTerms] = useState<MeddraTerm[]>([]);
  const [selected, setSelected] = useState<MeddraTerm | null>(null);
  const [pathId, setPathId] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const [reason, setReason] = useState('');
  const [manualTerm, setManualTerm] = useState('');
  const [manualCode, setManualCode] = useState('');
  const [preview, setPreview] = useState<MeddraTerm | undefined>(state.term);
  useEffect(() => {
    setPreview(state.term);
    if (!sharedPreview && state.action === CODING_ACTION.select && state.lltCode && state.version && !state.term) {
      let cancelled = false;
      dictionaryTerm(state.lltCode, state.version, state.language ?? 'en')
        .then(term => { if (!cancelled) setPreview(term); })
        .catch(e => { if (!cancelled) setError(`Proposal could not be verified: ${String(e)}`); });
      return () => { cancelled = true; };
    }
  }, [props.data]);
  useEffect(() => {
    if (dialog !== 'search') return;
    let cancelled = false;
    dictionaryReleases().then(items => {
      if (cancelled) return;
      setReleases(items);
      setVersion(current => current || items.find(item => item.active)?.version || '');
      if (!items.length) setError('Dictionary unavailable. You can still enter a value directly.');
    }).catch(e => { if (!cancelled) setError(`Dictionary unavailable: ${String(e)}. Direct entry remains available.`); });
    return () => { cancelled = true; };
  }, [dialog]);
  useEffect(() => {
    if (dialog !== 'search' || !version || !query.trim()) { setTerms([]); setHasMore(false); setSearching(false); return; }
    const controller = new AbortController();
    setTerms([]);
    setHasMore(false);
    setSearching(true);
    const timer = setTimeout(() => {
      dictionarySearch(query, version, 'en', offset, controller.signal)
        .then(result => { if (!controller.signal.aborted) { setTerms(result.terms ?? []); setHasMore(result.hasMore ?? false); setError(''); } })
        .catch(e => { if (!controller.signal.aborted) setError(`Dictionary search failed: ${String(e)}. You can enter a value directly.`); })
        .finally(() => { if (!controller.signal.aborted) setSearching(false); });
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [dialog, version, query, offset]);
  const write = (action: string, values: Partial<CodingState> = {}) => {
    field.proposal?.reject();
    field.update(JSON.stringify(codingCommand(state, verbatim, action, values)));
    setDialog(null);
  };
  const shownPreview = sharedPreview ? sharedPreview.term : preview;
  const openSearch = () => { setError(''); setQuery(state.raw?.[fields.term] || verbatim); setOffset(0); setSelected(null); setReason(''); setDialog('search'); };
  const openManual = () => { setError(''); setManualTerm(state.raw?.[fields.term] || shownPreview?.name || ''); setManualCode(state.raw?.[fields.code] || ''); setReason(''); setDialog('manual'); };
  const choose = (term: MeddraTerm) => {
    setSelected(term);
    const primary = (term.paths ?? []).filter(path => path.primarySoc);
    setPathId(primary.length === 1 ? primary[0].pathId ?? '' : '');
  };
  const useTerm = () => {
    if (!selected || !pathId) return;
    write(CODING_ACTION.select, { version: selected.version, language: selected.language, lltCode: selected.code, pathId, term: selected, reason });
  };
  const useManual = () => {
    if (!manualTerm.trim() && !manualCode.trim()) { setError('Enter a term or a code.'); return; }
    write(CODING_ACTION.manual, { raw: { [fields.term]: manualTerm, [fields.code]: manualCode }, reason });
  };
  const shownStatus = state.action ? `${state.action === 'manual' ? 'Manually entered · Unverified' : state.action === 'clear' ? 'Cleared' : 'Coding selected'} · Unsaved` : CODING_STATUS_LABELS[state.status ?? ''] ?? 'Not coded / unverified';
  return { field, visible: field.visible, editable: !!props.enabled && !field.disabledReason, state, preview: shownPreview, verbatim, displayCode: shownPreview?.code || state.raw?.[fields.code],
    shownStatus, displayTerm: state.raw?.[fields.term] || shownPreview?.name || (state.lltCode ? `MedDRA code ${state.lltCode} · Loading term…` : 'No dictionary term selected'),
    error: error || parsed.error || sharedPreview?.error || field.serverError, dialog, setDialog, openSearch, openManual, releases, version,
    changeVersion: (value: string) => { setVersion(value); setSelected(null); setOffset(0); }, query,
    changeQuery: (value: string) => { setQuery(value); setSelected(null); setOffset(0); }, terms, selected, choose, pathId, setPathId,
    hasMore, offset, searching, nextPage: () => setOffset(offset + 20), previousPage: () => setOffset(Math.max(0, offset - 20)),
    reason, setReason, manualTerm, setManualTerm, manualCode, setManualCode, useTerm, useManual,
    confirm: () => write(CODING_ACTION.confirm), clear: () => write(CODING_ACTION.clear),
  };
}
export type MeddraCodingBinding = ReturnType<typeof useMeddraCoding>;
